import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { GripVertical, MoreVertical, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import { UserWidget, getWidgetConfig } from '@/config/dashboardWidgets';
import { cn } from '@/lib/utils';

interface WidgetContainerProps {
  widget: UserWidget;
  onRemove: (id: string) => void;
  onResize: (id: string, size: 'full' | 'half' | 'third') => void;
  children: React.ReactNode;
}

export function WidgetContainer({ 
  widget, 
  onRemove, 
  onResize,
  children 
}: WidgetContainerProps) {
  const config = getWidgetConfig(widget.type);
  const Icon = config?.icon;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const sizeClasses = {
    full: 'col-span-1 md:col-span-2 lg:col-span-3',
    half: 'col-span-1 md:col-span-1 lg:col-span-2',
    third: 'col-span-1',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        sizeClasses[widget.size],
        isDragging && 'opacity-50 z-50'
      )}
    >
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="flex items-center gap-2">
              {Icon && <Icon className="h-4 w-4 text-primary" />}
              <h3 className="text-sm font-medium">{config?.name || 'Widget'}</h3>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onResize(widget.id, 'full')}>
                <Maximize2 className="h-4 w-4 mr-2" />
                Largura total
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onResize(widget.id, 'half')}>
                <Minimize2 className="h-4 w-4 mr-2" />
                Meia largura
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onResize(widget.id, 'third')}>
                <Minimize2 className="h-4 w-4 mr-2" />
                Um terço
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onRemove(widget.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remover
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          {children}
        </CardContent>
      </Card>
    </div>
  );
}
